/**
 * Addresses the register holds against many companies.
 *
 * The accountancy and company-formation problem, made visible: one firm
 * registers hundreds of companies and files its own address on all of them.
 * Excluding them from a campaign is the right default and hiding them is not -
 * one of these is worth a single letter written by a person, which is worth
 * more than forty automated ones. That sentence is the whole reason this tab
 * exists, so the list has to be browsable end to end rather than a top-25
 * preview beside a campaign's audience.
 *
 * ⚠ TWO UNITS ON ONE SCREEN. `total` counts ADDRESSES, `companies` counts
 * COMPANIES, and at threshold 5 they were 2 504 and 30 295 on the local mirror.
 * They are labelled separately for that reason; printing either under the
 * other's name is an order-of-magnitude error.
 *
 * ⚠ THIS IS NOT THE LEDGER. Most of these addresses have never been enrolled -
 * they were screened out before a ledger row existed. Opening one shows an
 * empty history, and that is the correct answer rather than a missing one.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Users2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { listSharedAddressPage, type SharedAddressPage } from "../../../../lib/marketingContacts";
import { Btn, EmptyState, MenuField, Toolbar, ToolbarGap, fmtDateTime, listCard } from "../../../ui";
import { Bar, Note, num } from "../shared";
import { showingRange } from "./lib";

/** Address, companies behind it, the share drawn. */
const COLS = "minmax(0,2.4fr) .9fr minmax(0,2fr)";

const PAGE_SIZE = 50;

/** The sender's own default is 5. The rest bracket it: 2 is "any sharing at
 *  all", 20 and 100 are the tail where a personal letter is worth writing. */
const THRESHOLDS = [2, 3, 5, 10, 20, 50, 100];

export function SharedTab({ onOpen }: { onOpen: (address: string) => void }) {
  const t = useT();

  const [minCompanies, setMinCompanies] = useState("5");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SharedAddressPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [minCompanies]);

  const load = useCallback(async () => {
    setData(null);
    try {
      setData(await listSharedAddressPage({ minCompanies: Number(minCompanies), page, limit: PAGE_SIZE }));
      setError(null);
    } catch {
      setError(t("marketing.error.shared"));
    }
  }, [minCompanies, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = data?.rows ?? [];
  // Scaled against the worst offender on the page rather than against the
  // threshold: the point of the list is the shape of the tail, and one address
  // behind a thousand companies is a different animal from one behind six.
  const worst = rows[0]?.companies ?? 1;
  const range = showingRange(data?.page ?? 1, PAGE_SIZE, rows.length);

  return (
    <>
      <Toolbar>
        <MenuField
          value={minCompanies}
          onChange={setMinCompanies}
          label={t("marketing.contacts.shared.threshold")}
          options={THRESHOLDS.map((n) => ({
            value: String(n),
            label: t("marketing.contacts.shared.thresholdOption", { min: n }),
          }))}
        />
        <ToolbarGap />
        <span className="crm-sub px-1.5">
          {data
            ? t("marketing.contacts.shared.totals", {
                addresses: num(data.total),
                companies: num(data.companies),
              })
            : t("common.loading")}
        </span>
      </Toolbar>

      {/* Three honest limits, said once above the list rather than repeated per
          row: what the numbers count, what the register actually holds, and how
          fresh the grouping is. */}
      <Note tone="info" className="mb-3.5">
        {t("marketing.contacts.shared.note")}
        {data ? (
          <>
            {" "}
            <span className="crm-mut">
              {t("marketing.contacts.shared.computedAt", { at: fmtDateTime(data.computedAt) })}
            </span>
          </>
        ) : null}
      </Note>

      {error ? (
        <Note tone="bad" className="mb-3.5">
          {error}
        </Note>
      ) : null}

      <div className="crm-card" style={listCard(COLS)}>
        {data === null && !error ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users2}
            title={t("marketing.contacts.shared.empty")}
            body={t("marketing.contacts.shared.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("marketing.contacts.shared.col.address")}</span>
              <span>{t("marketing.contacts.shared.col.companies")}</span>
              <span>{t("marketing.contacts.shared.col.share")}</span>
            </div>

            <div className="crm-rows pt-0">
              {rows.map((r) => (
                <div key={r.email} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="crm-av crm-av-soft">
                      <Users2 className="size-4" />
                    </span>
                    <button
                      type="button"
                      onClick={() => onOpen(r.email)}
                      className="crm-prow-n crm-trunc block min-w-0 text-left after:absolute after:inset-0 after:rounded-full"
                    >
                      {r.email}
                    </button>
                  </span>

                  <span className="crm-tnum text-[13px]">{num(r.companies)}</span>
                  <Bar value={r.companies} total={worst} tone="muted" className="max-w-[260px]" />
                </div>
              ))}
            </div>

            <div className="crm-tfoot">
              {t("marketing.contacts.showing", { from: range.from, to: range.to, total: data?.total ?? 0 })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn
                  small
                  disabled={!data || page >= data.pageCount}
                  onClick={() => setPage((p) => Math.min(data?.pageCount ?? p, p + 1))}
                >
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
}
